import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import RadarLabelEditor from "../agent-radar/RadarLabelEditor";
import { radarLabelHex } from "../../constants/agentRadarChoices";
import { formatNoteStamp, radarShortPosition, RADAR_TOAST_STYLE } from "../agent-radar/radarFormat";
import { ensureRadarAnim } from "./ClubRadarBits";
import { clubRadarService } from "../../services/clubRadarService";
import { CLUB_STATUS_ORDER, clubStatusMeta } from "../../constants/clubRadarChoices";
import {
  AvatarSquare,
  CrestChip,
  initialsOf,
  FlagChip,
  PositionPill,
  Pill,
  DashedAddPill,
  Eyebrow,
  SecondaryButton,
  PrimaryButton,
  IconSquareButton,
  GLYPH,
  RecommenderBanner,
  BannerActionButton,
  AttributionCaption,
  statusStyle,
  FONT_FAMILY,
} from "./v2";

// Club Radar entry detail — V2 rebuild from the artboards: desktop right drawer
// 460px (frame 1j) + mobile full sheet (frame 1k). Presentation only (Brief
// Rules 6/8): identity head + "Open profile" (F29), recommender banner with the
// permanent attribution + "Contact recommender" (F26/F27), the data-driven
// status segments with who-set-it attribution (F15 — FOUR statuses incl.
// `shortlisted`, mock counts are never limits), labels assign/unassign/create
// (F21–F23), IN LISTS + "+ Copy to list" (F16/F31 — mobile shows the artboard's
// "In N lists" pill), the reserved Reports socket (F30, no flow), the NOTES &
// ACTIVITY timeline (F25) and the note composer (F24). Fetches its own detail +
// timeline; mutations go through the SAME parent handlers as before and refresh
// the timeline. Mount contract unchanged — `currentUser` is a new OPTIONAL
// display-only prop (composer avatar, 1j); the drawer opens fine without it.

// Artboard label chips are r999 tint pills; real labels keep the platform's
// 6-colour palette (data-driven): hue dot + 8%-alpha fill, ✕ keeps F22.
const EntryLabelChip = ({ label, onRemove }) => {
  const hex = radarLabelHex(label.color);
  return (
    <Pill bg={`${hex}14`} color="#374151" pad="3px 9px" fontSize={11} gap={5} style={{ border: `1px solid ${hex}55` }}>
      <span style={{ width: 7, height: 7, borderRadius: "50%", background: hex, flex: "none" }} />
      {label.name}
      {onRemove && (
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${label.name} label`}
          className="border-none bg-transparent text-[#9CA3AF] hover:text-[#374151] cursor-pointer leading-none"
          style={{ fontSize: 11, padding: "0 0 0 2px" }}
        >
          ✕
        </button>
      )}
    </Pill>
  );
};

// Status segment control (frames 1j/1k, DESIGN-SPEC §4 item 6): equal-width
// buttons h38 (desktop) / h40 (mobile), r10; selected = success treatment
// (border 1.5 #16A34A, bg #ECFDF5, fg #047857, "✓ " prefix); unselected =
// white with the status-hue leading dot (7×7 desktop / 6×6 mobile).
const StatusSegment = ({ statusKey, label, active, mobile, onClick }) => {
  const dot = mobile ? 6 : 7;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className="cursor-pointer"
      style={{
        flex: "1 1 auto",
        minWidth: 0,
        height: mobile ? 40 : 38,
        boxSizing: "border-box",
        borderRadius: 10,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        padding: "0 8px",
        border: active ? "1.5px solid #16A34A" : "1px solid #E5E7EB",
        background: active ? "#ECFDF5" : "#FFFFFF",
        color: active ? "#047857" : "#6B7280",
        fontSize: mobile ? 12 : 13,
        fontWeight: active ? 700 : 600,
        whiteSpace: "nowrap",
        fontFamily: "inherit",
      }}
    >
      {active ? (
        <span style={{ fontWeight: 800 }}>{GLYPH.check}</span>
      ) : (
        <span style={{ width: dot, height: dot, borderRadius: "50%", background: statusStyle(statusKey).fg, flex: "none" }} />
      )}
      {label}
    </button>
  );
};

const ClubEntryDetail = ({
  entry,
  mobile,
  allLists = [],
  clubLabels = [],
  clubName,
  currentUser,
  onClose,
  onOpenProfile,
  onSetStatus,
  onAssignLabel,
  onUnassignLabel,
  onCreateLabel,
  onAddNote,
  onCopyToList,
  onContactRecommender,
}) => {
  const [detail, setDetail] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const [noteText, setNoteText] = useState("");
  const [posting, setPosting] = useState(false);
  const [labelOpen, setLabelOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);
  // Local status state so the segments update their selection + attribution live
  // (the `entry` prop is a snapshot; the hook mutates the list, not this object).
  const [localStatus, setLocalStatus] = useState(entry.status || "watching");
  const [statusMeta, setStatusMeta] = useState({
    by: entry.status_set_by_name,
    role: entry.status_set_by_role,
    at: entry.status_set_at,
  });
  const labelRef = useRef(null);
  const copyRef = useRef(null);

  useEffect(() => { ensureRadarAnim(); }, []);

  // Reset local status when a different entry opens in the same drawer instance.
  useEffect(() => {
    setLocalStatus(entry.status || "watching");
    setStatusMeta({ by: entry.status_set_by_name, role: entry.status_set_by_role, at: entry.status_set_at });
  }, [entry.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const refreshTimeline = useCallback(async () => {
    const res = await clubRadarService.getTimeline(entry.id);
    if (res?.success) setTimeline(res.data);
  }, [entry.id]);

  useEffect(() => {
    let alive = true;
    const controller = new AbortController();
    (async () => {
      const [d, t] = await Promise.all([
        clubRadarService.getEntry(entry.id, controller.signal),
        clubRadarService.getTimeline(entry.id, controller.signal),
      ]);
      if (!alive) return;
      if (d.success) setDetail(d.data);
      if (t.success) setTimeline(t.data);
    })().catch(() => {});
    return () => {
      alive = false;
      controller.abort();
    };
  }, [entry.id]);

  useEffect(() => {
    const onDown = (e) => {
      if (labelRef.current && !labelRef.current.contains(e.target)) setLabelOpen(false);
      if (copyRef.current && !copyRef.current.contains(e.target)) setCopyOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const player = detail?.player || entry.player || entry;
  const recommender = detail?.recommender;
  const requestTitle = detail?.request_title;
  const inLists = detail?.in_lists || [];
  const assignedLabels = detail?.labels || entry.labels || [];
  const status = localStatus;
  const statusBy = statusMeta.by;
  const statusRole = statusMeta.role;
  const statusAt = statusMeta.at;

  const availableLabels = clubLabels.filter((cl) => !assignedLabels.some((al) => al.id === cl.id));

  const handleStatus = async (s) => {
    if (s === localStatus) return;
    const prev = { status: localStatus, meta: statusMeta };
    setLocalStatus(s); // optimistic — segment highlight moves immediately
    const res = await onSetStatus(entry.id, s);
    if (res?.success) {
      const d = res.data || {};
      setStatusMeta({
        by: d.status_set_by_name ?? statusMeta.by,
        role: d.status_set_by_role ?? statusMeta.role,
        at: d.status_set_at ?? new Date().toISOString(),
      });
      toast("Status updated", { position: "bottom-center", style: RADAR_TOAST_STYLE });
      refreshTimeline();
    } else {
      setLocalStatus(prev.status); // revert
      setStatusMeta(prev.meta);
      toast.error(res?.error || "Could not set the status.");
    }
  };

  const handleAssign = async (label) => {
    setLabelOpen(false);
    await onAssignLabel(entry.id, label);
    setDetail((d) => (d ? { ...d, labels: [...(d.labels || []), label] } : d));
  };
  const handleUnassign = async (label) => {
    await onUnassignLabel(entry.id, label);
    setDetail((d) => (d ? { ...d, labels: (d.labels || []).filter((l) => l.id !== label.id) } : d));
  };

  const postNote = async () => {
    const v = noteText.trim();
    if (!v || posting) return;
    setPosting(true);
    const res = await onAddNote(entry.id, v);
    setPosting(false);
    if (res?.success) {
      setNoteText("");
      refreshTimeline();
    }
  };

  // ── artboard-derived look values (1j desktop / 1k mobile) ──────────────────
  const width = mobile ? "100%" : 460;
  const pos = radarShortPosition(player.position);
  const playerClub = player.club_name || player.club || "";
  const playerFlag = player.nationality_flag;
  // Rule 3 (flag exception): image flags only — never emoji, never faked chips.
  const flagSrc = playerFlag && /^https?:\/\//.test(playerFlag) ? playerFlag : null;
  const noteAv = mobile ? 24 : 26;
  const eventDot = mobile ? 6 : 7;

  // "+ Label" trigger + popover (existing editor, same handlers F21/F23).
  const labelTrigger = (
    <div ref={labelRef} style={{ position: "relative", display: "inline-flex" }}>
      <DashedAddPill role="button" aria-label="Add label" onClick={() => setLabelOpen((v) => !v)}>
        {GLYPH.plus} Label
      </DashedAddPill>
      {labelOpen && (
        <div style={{ position: "absolute", right: 0, top: 28, zIndex: 40, width: 210, background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12, boxShadow: "0 14px 34px rgba(15,23,42,.16)", padding: 4, animation: "tp-pop 0.12s ease" }}>
          <RadarLabelEditor availableLabels={availableLabels} onAssign={handleAssign} onCreate={onCreateLabel} />
        </div>
      )}
    </div>
  );

  // "+ Copy to list" trigger + popover (same handler F16; optimistic in_lists).
  const copyTrigger = (
    <div ref={copyRef} style={{ position: "relative", display: "inline-flex" }}>
      <DashedAddPill role="button" aria-label="Copy to list" onClick={() => setCopyOpen((v) => !v)}>
        {GLYPH.plus} Copy to list
      </DashedAddPill>
      {copyOpen && (
        <div style={{ position: "absolute", right: 0, top: 28, zIndex: 40, width: 200, maxHeight: 220, overflowY: "auto", background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 12, boxShadow: "0 14px 34px rgba(15,23,42,.16)", padding: 4, animation: "tp-pop 0.12s ease" }}>
          {allLists.filter((l) => !inLists.some((il) => il.id === l.id)).map((l) => (
            <button
              key={l.id}
              type="button"
              onClick={() => { setCopyOpen(false); onCopyToList(entry, l); setDetail((d) => (d ? { ...d, in_lists: [...(d.in_lists || []), { id: l.id, name: l.name, share_tier: l.share_tier }] } : d)); }}
              className="w-full text-left cursor-pointer"
              style={{ display: "block", padding: "8px 10px", fontSize: 12.5, color: "#374151", background: "none", border: "none", borderRadius: 8, fontFamily: "inherit" }}
            >
              {l.name}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  const body = (
    <div
      style={{
        width,
        maxWidth: "100%",
        height: "100%",
        background: "#FFFFFF",
        display: "flex",
        flexDirection: "column",
        fontFamily: FONT_FAMILY,
        boxShadow: mobile ? "none" : "-8px 0 32px rgba(17,24,39,0.12)",
        animation: mobile ? "tp-sheet 0.2s ease" : undefined,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Drawer header — 1j: eyebrow · × / 1k: ‹ · eyebrow. The artboard's ⋯
          trigger was removed entirely (R1, Marin 2026-07-24): no entry-level
          menu exists behind it — row actions stay on the list row's ⋯. */}
      <div style={{ display: "flex", alignItems: "center", gap: mobile ? 10 : 8, padding: mobile ? "12px 16px" : "14px 20px", borderBottom: "1px solid #E5E7EB", flexShrink: 0 }}>
        {mobile && (
          <IconSquareButton size={30} radius={9} color="#374151" fontSize={16} aria-label="Back" onClick={onClose}>
            {GLYPH.chevronLeft}
          </IconSquareButton>
        )}
        <Eyebrow fontSize={12} spacing=".06em">Radar entry</Eyebrow>
        {!mobile && (
          <IconSquareButton size={30} radius={9} fontSize={15} aria-label="Close" onClick={onClose} style={{ marginLeft: "auto" }}>
            {GLYPH.close}
          </IconSquareButton>
        )}
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: "auto", padding: mobile ? 16 : "18px 20px", display: "flex", flexDirection: "column", gap: mobile ? 14 : 16 }}>
        {/* 1. Identity row (person-first; F29 Open profile — desktop keeps the
            artboard's button, mobile makes the existing name/avatar identity
            block tappable (R2, Marin 2026-07-24): same onOpenProfile handler
            and /search/player/{id} route as desktop, no new surface). */}
        <div
          role={mobile ? "button" : undefined}
          tabIndex={mobile ? 0 : undefined}
          aria-label={mobile ? "Open profile" : undefined}
          onClick={mobile ? () => onOpenProfile(entry) : undefined}
          onKeyDown={mobile ? (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpenProfile(entry); } } : undefined}
          className={mobile ? "cursor-pointer" : undefined}
          style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}
        >
          <AvatarSquare
            name={player.name}
            initials={player.initials}
            imageUrl={player.image_url}
            size={mobile ? 46 : 52}
            radius={mobile ? 13 : 14}
            fontSize={mobile ? 14 : 16}
          />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
              <span style={{ fontSize: mobile ? 17 : 19, fontWeight: 800, letterSpacing: "-0.2px", color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {player.name || "Player"}
              </span>
              {pos && <PositionPill pad={mobile ? "2px 7px" : "2px 8px"} fontSize={mobile ? 10.5 : 11}>{pos}</PositionPill>}
              <FlagChip src={flagSrc} size={mobile ? "row" : "drawer"} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 5, marginTop: 4, minWidth: 0 }}>
              {playerClub && (
                <CrestChip name={playerClub} logoUrl={player.club_crest_url} size={mobile ? 15 : 16} radius={4} fontSize={mobile ? 7 : 7.5} />
              )}
              <span style={{ fontSize: mobile ? 12 : 12.5, color: "#6B7280", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {[playerClub, player.age].filter((x) => x !== null && x !== undefined && x !== "").join(" · ")}
              </span>
            </div>
          </div>
          {!mobile && (
            <SecondaryButton height={34} pad="0 13px" fontSize={12.5} onClick={() => onOpenProfile(entry)}>
              Open profile
            </SecondaryButton>
          )}
        </div>

        {/* 2. Recommender banner (F26/F27 — permanent attribution, R2/R15) */}
        {recommender && (
          <RecommenderBanner
            mobile={mobile}
            avatarInitials={recommender.initials || initialsOf(recommender.name)}
            avatarImageUrl={recommender.avatar_url}
            title={`Recommended by ${recommender.name}`}
            agentPillLabel={mobile ? undefined : "Agent"}
            sub={[recommender.agency, requestTitle ? `from ${GLYPH.quoteOpen}${requestTitle}${GLYPH.quoteClose}` : null].filter(Boolean).join(" · ")}
            action={
              detail?.contact_available !== false ? (
                <BannerActionButton
                  height={mobile ? 28 : 30}
                  radius={mobile ? 8 : 9}
                  fontSize={mobile ? 11 : 11.5}
                  style={mobile ? { padding: "0 10px" } : undefined}
                  onClick={() => onContactRecommender(entry)}
                >
                  {mobile ? "Contact" : "Contact recommender"}
                </BannerActionButton>
              ) : null
            }
          />
        )}

        {/* 3. Status segments (F15 — four data-driven statuses, R7) */}
        <div>
          {!mobile && (
            <Eyebrow fontSize={11} spacing=".07em" style={{ marginBottom: 8 }}>
              Status {GLYPH.emDash} visible to colleagues
            </Eyebrow>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: mobile ? 7 : 8 }}>
            {CLUB_STATUS_ORDER.map((s) => (
              <StatusSegment
                key={s}
                statusKey={s}
                label={clubStatusMeta(s).label}
                active={status === s}
                mobile={mobile}
                onClick={() => handleStatus(s)}
              />
            ))}
          </div>
          {statusBy && (
            <AttributionCaption fontSize={mobile ? 10.5 : 11} style={{ marginTop: 6 }}>
              Set by {statusBy}
              {statusRole ? ` · ${statusRole}` : ""}
              {statusAt ? ` · ${formatNoteStamp(statusAt)}` : ""}
              {mobile ? " · visible to colleagues" : ""}
            </AttributionCaption>
          )}
        </div>

        {/* 4. Labels row (F21/F22/F23) — 1k merges the "In N lists" pill in */}
        <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, rowGap: 8 }}>
          {!mobile && (
            <Eyebrow fontSize={11} spacing=".07em" style={{ marginRight: 4 }}>Labels</Eyebrow>
          )}
          {assignedLabels.map((l) => (
            <EntryLabelChip key={l.id} label={l} onRemove={() => handleUnassign(l)} />
          ))}
          {assignedLabels.length === 0 && (
            <span style={{ fontSize: 12, color: "#9CA3AF" }}>No labels</span>
          )}
          {mobile && inLists.length > 0 && (
            <Pill bg="#E8F1FF" color="#1B6FE0" pad="3px 9px" fontSize={11}>
              In {inLists.length} {inLists.length === 1 ? "list" : "lists"}
            </Pill>
          )}
          {labelTrigger}
          {/* Mobile keeps the existing copy-to-list function (F16) in the
              artboard's dashed-pill pattern — 1k defines no other placement. */}
          {mobile && copyTrigger}
        </div>

        {/* 5. In-lists row (F31 — desktop; mobile shows the count pill above) */}
        {!mobile && (
          <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 6, rowGap: 8 }}>
            <Eyebrow fontSize={11} spacing=".07em" style={{ marginRight: 4 }}>In lists</Eyebrow>
            {inLists.map((l) => (
              <Pill key={l.id} bg="#E8F1FF" color="#1B6FE0" pad="3px 9px" fontSize={11}>{l.name}</Pill>
            ))}
            {inLists.length === 0 && <span style={{ fontSize: 12, color: "#9CA3AF" }}>None</span>}
            {copyTrigger}
          </div>
        )}

        {/* 6. Reserved Reports socket (F30, D31/R13 — no flow behind it) */}
        <div style={{ border: "1.5px dashed #D8DEE7", borderRadius: 12, padding: mobile ? "11px 13px" : "13px 14px", background: "#FBFCFE", boxSizing: "border-box" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Eyebrow fontSize={mobile ? 10.5 : 11} spacing=".07em" color="#6B7280">Reports</Eyebrow>
            <Pill bg="#F3F4F6" color="#6B7280" pad="2px 8px" fontSize={mobile ? 9.5 : 10} style={{ textTransform: "uppercase" }}>
              Coming soon
            </Pill>
          </div>
          <div style={{ marginTop: mobile ? 4 : 5, fontSize: mobile ? 11.5 : 12, color: "#9CA3AF", lineHeight: 1.5 }}>
            {mobile
              ? `No reports yet ${GLYPH.emDash} ScoutPitch reports will attach here.`
              : `No reports yet ${GLYPH.emDash} scouting reports from ScoutPitch will attach to this entry.`}
          </div>
        </div>

        {/* 7. Notes & activity timeline (F25, R12) */}
        <div style={{ flex: 1 }}>
          <Eyebrow fontSize={mobile ? 10.5 : 11} spacing=".07em" style={{ marginBottom: mobile ? 9 : 10 }}>
            Notes &amp; activity
          </Eyebrow>
          <div style={{ display: "flex", flexDirection: "column", gap: mobile ? 11 : 12 }}>
            {timeline.length === 0 && <div style={{ fontSize: 12, color: "#9CA3AF" }}>No activity yet.</div>}
            {timeline.map((t, i) =>
              t.kind === "event" ? (
                <div key={i} style={{ display: "flex", gap: 10 }}>
                  <div style={{ width: noteAv, flex: "none", display: "flex", justifyContent: "center", paddingTop: 5 }}>
                    <span style={{ width: eventDot, height: eventDot, borderRadius: "50%", background: "#CBD5E1" }} />
                  </div>
                  <div style={{ minWidth: 0, flex: 1 }}>
                    <div style={{ fontSize: mobile ? 12 : 12.5, color: "#374151" }}>{t.text}</div>
                    {(t.by_name || t.at) && (
                      <div style={{ marginTop: 2, fontSize: mobile ? 10 : 10.5, color: "#9CA3AF" }}>
                        {[t.by_name, t.at ? formatNoteStamp(t.at) : null].filter(Boolean).join(" · ")}
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div key={i} style={{ display: "flex", gap: 10 }}>
                  <AvatarSquare
                    name={t.by_name}
                    initials={t.initials}
                    size={noteAv}
                    radius={8}
                    fontSize={mobile ? 8.5 : 9}
                    fontWeight={800}
                  />
                  <div style={{ flex: 1, minWidth: 0, background: "#F8FAFC", border: "1px solid #EEF2F6", borderRadius: 12, padding: mobile ? "9px 11px" : "10px 12px", boxSizing: "border-box" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 6, minWidth: 0 }}>
                      <span style={{ fontSize: mobile ? 12 : 12.5, fontWeight: 700, color: "#111827", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {t.by_name}
                      </span>
                      {t.by_role && (
                        <Pill bg="#F3F4F6" color="#4B5563" pad={mobile ? "1px 6px" : "1px 7px"} fontSize={mobile ? 9 : 9.5}>
                          {t.by_role}
                        </Pill>
                      )}
                      {t.at && (
                        <span style={{ marginLeft: "auto", flex: "none", fontSize: mobile ? 10 : 10.5, color: "#9CA3AF" }}>
                          {formatNoteStamp(t.at)}
                        </span>
                      )}
                    </div>
                    <div style={{ marginTop: mobile ? 4 : 5, fontSize: mobile ? 12 : 12.5, color: "#374151", lineHeight: 1.5, whiteSpace: "pre-wrap" }}>
                      {t.text}
                    </div>
                  </div>
                </div>
              ),
            )}
          </div>
        </div>
      </div>

      {/* Composer footer (F24) — 1j: avatar + input h38 + Post; 1k: input h42 + Post */}
      <div style={{ borderTop: "1px solid #E5E7EB", padding: mobile ? "11px 16px 16px" : "12px 20px", background: "#FFFFFF", flexShrink: 0, display: "flex", gap: 8, alignItems: "flex-end" }}>
        {!mobile && currentUser?.name && (
          <AvatarSquare
            name={currentUser.name}
            imageUrl={currentUser.image_url || currentUser.avatar_url}
            size={28}
            radius={9}
            fontSize={9}
            fontWeight={800}
            style={{ marginBottom: 5 }}
          />
        )}
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) postNote(); }}
          rows={1}
          placeholder={mobile ? `Add a note${GLYPH.ellipsis}` : `Add a note ${GLYPH.emDash} visible to ${clubName || "club"} staff`}
          className="flex-1 outline-none resize-none"
          style={{ minWidth: 0, border: "1px solid #E5E7EB", borderRadius: mobile ? 11 : 10, padding: "9px 12px", fontSize: 12.5, minHeight: mobile ? 42 : 38, maxHeight: 96, boxSizing: "border-box", fontFamily: "inherit", color: "#111827" }}
        />
        <PrimaryButton
          height={mobile ? 42 : 38}
          radius={mobile ? 11 : 10}
          onClick={postNote}
          disabled={!noteText.trim() || posting}
        >
          Post
        </PrimaryButton>
      </div>
    </div>
  );

  return createPortal(
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 70,
        background: "rgba(17,24,39,0.35)",
        display: "flex",
        justifyContent: mobile ? "center" : "flex-end",
        alignItems: "stretch",
      }}
    >
      {body}
    </div>,
    document.body,
  );
};

export default ClubEntryDetail;
